import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { OrderStage } from "./helpers";

type OrderStatusTextProps = {
  stage: OrderStage;
  supplierName: string;
  buyerName: string;
  cancelledBy: OrderGetResponseType["cancelledBy"];
  fulfillmentType?: OrderGetResponseType["fulfillmentType"];
  cancellationReason: string | null;
  createdAt: Date | null;
  acceptedAt: Date | null;
  cancelledAt: Date | null;
  voidedAt: Date | null;
  deliveredAt: Date | null;
  deliveryPlannedAt: Date | null;
  dispatchPlannedAt: Date | null;
};
export const OrderStatusText = (props: OrderStatusTextProps): ReactElement => {
  const dateFormat = "DD/MM/YY";
  const dateAndTimeFormat = `${dateFormat} HH:mm A`;
  const { business } = useSellarCurrentBusiness();
  const cancellationDate = props.cancelledAt
    ? dayjs(props.cancelledAt).format(dateAndTimeFormat)
    : null;
  const voidedDate = props.voidedAt
    ? dayjs(props.voidedAt).format(dateAndTimeFormat)
    : null;

  const requestDate = dayjs(props.createdAt).format(dateAndTimeFormat);
  const requestedDaysAgo = dayjs(props.createdAt).fromNow();
  const acceptedDate = dayjs(props.acceptedAt).format(dateAndTimeFormat);
  const acceptedDaysAgo = dayjs(props.acceptedAt).fromNow();

  const deliveryDate = () => {
    if (props.cancelledAt) return "Order cancelled";
    return dayjs(props.deliveredAt ?? props.deliveryPlannedAt).format(
      dateFormat,
    );
  };

  const dispatchDate = () => {
    if (props.cancelledAt) return "Order cancelled";
    return dayjs(props.deliveredAt ?? props.dispatchPlannedAt).format(
      dateFormat,
    );
  };

  switch (props.stage) {
    case "Requested":
      return (
        <small className={"text-zinc-500"}>
          Requested on {requestDate} ({requestedDaysAgo}).
        </small>
      );
    case "Accepted":
      return (
        <small className={"text-zinc-500"}>
          Accepted on {acceptedDate} ({acceptedDaysAgo}).
        </small>
      );
    case "Confirmed": {
      if (!props.deliveredAt && !props.deliveryPlannedAt) {
        return (
          <small className="text-zinc-500">
            {props.fulfillmentType === "collection" ? "Collection" : "Delivery"}{" "}
            date: TBC by {props.supplierName}
          </small>
        );
      }

      if (props.fulfillmentType === "sellarDistribution") {
        return (
          <small className="text-zinc-500">
            Collection set for {dispatchDate()}.
          </small>
        );
      }

      return (
        <small className="text-zinc-500">
          {[
            business?.Retailer && props.fulfillmentType === "collection"
              ? `You'll be able to collect it on ${deliveryDate()}.`
              : `${
                  props.fulfillmentType === "collection"
                    ? "Collection"
                    : "Delivery"
                } is set for ${deliveryDate()}.`,
          ].join(" ")}
        </small>
      );
    }
    case "Dispatched": {
      if (!props.deliveredAt && !props.deliveryPlannedAt) {
        return (
          <small className="text-zinc-500">
            Delivery date: TBC by {props.supplierName}`
          </small>
        );
      }
      return (
        <small className="text-zinc-500">
          Delivery is set for {deliveryDate()}.
        </small>
      );
    }
    case "Delivered":
      return (
        <small className="text-zinc-500">
          {props.fulfillmentType === "collection" ? "Collected" : "Delivered"}{" "}
          on {deliveryDate()}.
        </small>
      );
    case "Cancelled":
      return (
        <small className="text-zinc-500">Cancelled on {cancellationDate}</small>
      );
    case "Voided":
      return <small className="text-zinc-500">Voided on {voidedDate}</small>;
  }
};
